import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Auth } from "../services/api";
import { FaUser, FaEnvelope, FaLock, FaSpinner, FaCheck, FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";
import logo from "../logo.svg";

const USERNAME_REGEX = /^[a-zA-Z]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Signup = () => {
  const [formData, setFormData] = useState({ username: "", email: "", password: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ username: false, email: false, password: false });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const usernameErrors = useMemo(() => {
    const val = formData.username;
    const errs = [];
    if (val.length === 0) errs.push("Username is required");
    else {
      if (val.length < 3) errs.push("Must be at least 3 characters");
      if (/\s/.test(val)) errs.push("Must not contain spaces");
      if (/\d/.test(val)) errs.push("Must not contain digits");
      if (/[^a-zA-Z0-9]/.test(val) && !/\s/.test(val)) errs.push("Must not contain special characters");
      if (!USERNAME_REGEX.test(val) && val.length >= 3 && errs.length === 1)
        errs.push("Only letters (a\u2013z) are allowed");
    }
    return errs;
  }, [formData.username]);

  const emailErrors = useMemo(() => {
    const val = formData.email;
    if (val.length === 0) return ["Email is required"];
    if (!EMAIL_REGEX.test(val)) return ["Please enter a valid email address (e.g. name@company.com)"];
    return [];
  }, [formData.email]);

  const passwordChecks = useMemo(() => {
    const val = formData.password;
    return [
      { label: "At least 8 characters", met: val.length >= 8 },
      { label: "Contains an uppercase letter (A\u2013Z)", met: /[A-Z]/.test(val) },
      { label: "Contains a lowercase letter (a\u2013z)", met: /[a-z]/.test(val) },
      { label: "Contains a digit (0\u20139)", met: /\d/.test(val) },
      { label: "Contains a special character (!@#$%...)", met: /[^a-zA-Z0-9\s]/.test(val) },
    ];
  }, [formData.password]);

  const passwordValid = passwordChecks.every((c) => c.met);
  const usernameValid = usernameErrors.length === 0;
  const emailValid = emailErrors.length === 0;
  const allValid = usernameValid && emailValid && passwordValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setMessage("");
    setError("");
    setLoading(true);

    try {
      const res = await Auth.register(formData);
      setMessage(res.data?.message || "Registered successfully. Check your email.");
      setFormData({ username: "", email: "", password: "" });
      setTouched({ username: false, email: false, password: false });
    } catch (err) {
      const data = err.response?.data;
      if (data?.email?.[0]?.toLowerCase().includes("already")) {
        setError("An account with this email already exists. Please log in instead.");
      } else if (data?.username) {
        const msg = data.username[0] || "";
        if (msg.toLowerCase().includes("already")) {
          setError("This username is already taken. Please choose another.");
        } else {
          setError(msg || "Username is invalid. Use only letters with no spaces.");
        }
      } else if (typeof data === "string") {
        setError(data);
      } else {
        setError(data?.error || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fieldBorder = (fieldName, isValid) => {
    if (!touched[fieldName] && formData[fieldName].length === 0)
      return "border-blue-400/30";
    return isValid ? "border-green-400/60" : "border-red-400/60";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-gray-900 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 md:p-12 max-w-md w-full z-10 relative mx-4">
        <div className="text-center mb-8">
          <img src={logo} alt="Logo" className="h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-2">Join Us</h2>
          <p className="text-blue-200 text-sm">Create your new account</p>
        </div>

        {message && !loading && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-100 px-4 py-3 rounded-lg mb-6 text-sm text-center">
            {message}
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-100 px-4 py-3 rounded-lg mb-6 text-sm text-center">
            {error}
          </div>
        )}

        {loading && (
          <div className="bg-blue-500/20 border border-blue-500/50 text-blue-100 px-4 py-3 rounded-lg mb-6 flex items-center justify-center text-sm">
            <FaSpinner className="animate-spin mr-2" />
            Creating Account...
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Username */}
          <div>
            <label className="block text-blue-100 text-xs font-bold mb-2 uppercase tracking-wide">Username</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaUser className="text-blue-300" />
              </div>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                className={`w-full pl-10 pr-4 py-3 bg-white/5 border ${fieldBorder("username", usernameValid)} rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 text-white placeholder-blue-300/50 transition-all duration-200`}
                placeholder="johndoe"
              />
            </div>
            {touched.username && !usernameValid && (
              <div className="mt-1.5 space-y-0.5">
                {usernameErrors.map((err, i) => (
                  <p key={i} className="text-red-300 text-xs flex items-start gap-1.5">
                    <FaTimes className="mt-0.5 flex-shrink-0" size={10} />
                    <span>{err}</span>
                  </p>
                ))}
              </div>
            )}
            {!touched.username && formData.username.length === 0 && (
              <p className="text-blue-300/60 text-xs mt-1.5">Letters only, no spaces, digits, or special characters</p>
            )}
            {touched.username && usernameValid && (
              <p className="text-green-300 text-xs mt-1.5 flex items-center gap-1.5">
                <FaCheck size={10} /> Username looks good
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-blue-100 text-xs font-bold mb-2 uppercase tracking-wide">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaEnvelope className="text-blue-300" />
              </div>
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                className={`w-full pl-10 pr-4 py-3 bg-white/5 border ${fieldBorder("email", emailValid)} rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 text-white placeholder-blue-300/50 transition-all duration-200`}
                placeholder="name@company.com"
              />
            </div>
            {touched.email && !emailValid && (
              <div className="mt-1.5">
                {emailErrors.map((err, i) => (
                  <p key={i} className="text-red-300 text-xs flex items-start gap-1.5">
                    <FaTimes className="mt-0.5 flex-shrink-0" size={10} />
                    <span>{err}</span>
                  </p>
                ))}
              </div>
            )}
            {touched.email && emailValid && (
              <p className="text-green-300 text-xs mt-1.5 flex items-center gap-1.5">
                <FaCheck size={10} /> Valid email address
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-blue-100 text-xs font-bold mb-2 uppercase tracking-wide">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="text-blue-300" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
                className={`w-full pl-10 pr-12 py-3 bg-white/5 border ${fieldBorder("password", passwordValid)} rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 text-white placeholder-blue-300/50 transition-all duration-200`}
                placeholder="Create a strong password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-blue-300 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>

            {(touched.password || formData.password.length > 0) && (
              <div className="mt-2 space-y-1 bg-white/5 rounded-lg p-3">
                <p className="text-blue-200 text-xs font-semibold mb-1.5">Password must contain:</p>
                {passwordChecks.map((check, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {check.met ? (
                      <FaCheck className="text-green-400 flex-shrink-0" size={10} />
                    ) : (
                      <FaTimes className="text-red-400 flex-shrink-0" size={10} />
                    )}
                    <span className={`text-xs ${check.met ? "text-green-300" : "text-red-300"}`}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!touched.password && formData.password.length === 0 && (
              <p className="text-blue-300/60 text-xs mt-1.5">Must include upper/lowercase, digit, and special character</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !allValid}
            className={`w-full font-bold py-3 px-4 rounded-lg shadow-lg transform transition-all duration-200 ${
              allValid && !loading
                ? "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white hover:scale-[1.02] active:scale-95 cursor-pointer"
                : "bg-gray-600/50 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? "Creating Account..." : "Sign Up"}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-blue-200 text-sm">
            Already have an account?{" "}
            <Link to="/login" className="text-white font-semibold hover:underline ml-1">
              Log In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
